import "./site/error-beacon";
import { Maw, MawResult, RUN_MS } from "./games/maw/maw";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import { VirtualJoystick } from "./ui/joystick";
import "./ui/styles.css";

const PB_KEY = "aether.maw_pb";
const HANDLE_KEY = "aether.handle";

flushFailedSubmits("maw");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startMawRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/maw", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishMawRun(
  runId: string, handle: string,
  samples: MawResult["samples"],
  eats: MawResult["eatEvents"],
) {
  const url = `/api/runs/maw/${runId}`;
  const body = { handle, samples, eats };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("maw", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("maw", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudRadius = document.getElementById("hud-radius")!;
const hudTime = document.getElementById("hud-time")!;
const hudEats = document.getElementById("hud-eats")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Maw | null = null;
let activeRunId: string | null = null;
let pendingResult: MawResult | null = null;

// Mobile: mount the shared VirtualJoystick. CSS gates display:block on
// (pointer: coarse) — desktop never sees it. Joystick gives world-y-up;
// Maw expects screen-y-down (positive iy = move down on screen), so we
// flip y before forwarding.
const joystick = new VirtualJoystick(document.body, (x, y) => {
  if (game) game.setTouchVec(x, -y);
});
// Avoid TS unused-binding complaint while retaining the reference so
// the joystick survives module-init garbage collection.
void joystick;

async function startRun() {
  if (game) game.dispose();
  const runStart = await startMawRun();
  activeRunId = runStart?.run_id ?? null;
  const seed = runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Maw(canvas, {
    onHud: (runMs, radius, eats, scoreEstimate) => {
      hudRadius.textContent = radius.toFixed(2);
      hudTime.textContent = ((RUN_MS - runMs) / 1000).toFixed(1);
      hudEats.textContent = eats.toString();
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async (result) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      const biteNote = result.hunter_bites > 0 ? ` · ${result.hunter_bites} bite${result.hunter_bites === 1 ? "" : "s"}` : "";
      msgBody.textContent = `${result.scoreEstimate.toLocaleString()} pts · peak r=${result.peak_radius.toFixed(2)} · ${result.eats} eats${biteNote}`;
      btnStart.textContent = "Try again";

      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (result.scoreEstimate > pb) localStorage.setItem(PB_KEY, String(result.scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "maw", score: result.scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishMawRun(activeRunId, handle, result.samples, result.eatEvents);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingResult = result;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Maw";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingResult) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishMawRun(activeRunId, value, pendingResult.samples, pendingResult.eatEvents);
  pendingResult = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
